import React, { useEffect, useState } from 'react';
import { Trophy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function diff(target) {
  const now = new Date();
  const d = Math.max(0, target - now);
  return {
    days: Math.floor(d / (1000 * 60 * 60 * 24)),
    hrs: Math.floor((d / (1000 * 60 * 60)) % 24),
    mins: Math.floor((d / (1000 * 60)) % 60),
    secs: Math.floor((d / 1000) % 60),
  };
}

export default function SeasonCountdown() {
  const [target, setTarget] = useState(null);
  const [label, setLabel] = useState('Patriots Season Kickoff');
  const [visibleFrom, setVisibleFrom] = useState(null);
  const [t, setT] = useState({ days: 0, hrs: 0, mins: 0, secs: 0 });

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const resp = await fetch(`${API}/season`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (ignore) return;
        setTarget(new Date(`${data.targetDate}T00:00:00`));
        setLabel(data.label);
        if (data.visibleFrom) setVisibleFrom(new Date(`${data.visibleFrom}T00:00:00`));
      } catch (_e) { /* silent */ }
    })();
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    if (!target) return;
    setT(diff(target));
    const id = setInterval(() => setT(diff(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  if (!target) return null;
  // Hide until visibleFrom date arrives
  if (visibleFrom && new Date() < visibleFrom) return null;

  const cells = [
    { label: 'Days', v: t.days },
    { label: 'Hrs', v: t.hrs },
    { label: 'Mins', v: t.mins },
    { label: 'Secs', v: t.secs }
  ];

  const targetLabel = target.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <section className="relative py-12 md:py-16 bg-gradient-to-br from-[#0a1f44] via-[#0d2555] to-[#0a1f44] border-y-4 border-[#c8102e] overflow-hidden" data-testid="season-countdown">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#c8102e]/15 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="relative max-w-5xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 bg-[#c8102e] text-white font-heading uppercase tracking-[0.3em] text-[10px] px-3 py-1.5 mb-4">
          <Trophy className="w-3 h-3" /> Season Countdown
        </div>
        <h2 className="font-display text-white text-3xl md:text-5xl leading-tight" data-testid="season-countdown-label">{label.toUpperCase()}</h2>
        <div className="mt-2 font-heading uppercase tracking-widest text-[#c8102e] text-xs" data-testid="season-countdown-target-date">{targetLabel}</div>

        <div className="grid grid-cols-4 gap-3 md:gap-5 mt-8 max-w-3xl mx-auto">
          {cells.map((c) => (
            <div key={c.label} className="bg-black/30 border border-white/10 backdrop-blur-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#c8102e]" />
              <div className="py-6 md:py-8 text-center">
                <div className="font-display text-4xl md:text-6xl text-white tabular-nums" data-testid={`season-countdown-${c.label.toLowerCase()}`}>{String(c.v).padStart(2, '0')}</div>
                <div className="font-heading uppercase text-[10px] md:text-xs tracking-[0.3em] text-white/60 mt-1">{c.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
